import { CheckIcon, SearchIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { DatePicker } from '../date-time';

import { defaultDatePresets } from './filter-summary';
import { toDisplayBound, toStoredBound } from './money-range';
import type {
  AsyncFilterDef,
  BooleanFilterDef,
  DateRangeFilterDef,
  EnumFilterDef,
  FilterDef,
  FilterOption,
  FilterValue,
  NumberRangeFilterDef,
  TextFilterDef,
} from './types';
import { useDebouncedSearch } from './use-debounced-search';
import { useFilterOptions } from './use-filter-options';
import { useResolvedOptions } from './use-resolved-options';

export interface DataTableFilterPickerProps {
  def: FilterDef<never>;
  value: FilterValue | undefined;
  /** `undefined` clears the filter. Option lists apply on every toggle, as Shopify's do. */
  onChange: (next: FilterValue | undefined) => void;
  /** The edit is finished: a single choice was made, or Enter / Apply. */
  onDone?: () => void;
}

/** The editor for one filter's value; its UI depends on the filter type. */
function DataTableFilterPicker({ def, value, onChange, onDone }: DataTableFilterPickerProps) {
  const shared = { value, onChange, onDone };
  switch (def.type) {
    case 'enum':
      return <EnumPicker def={def} {...shared} />;
    case 'async':
      return <AsyncPicker def={def} {...shared} />;
    case 'text':
      return <TextPicker def={def} {...shared} />;
    case 'boolean':
      return <BooleanPicker def={def} {...shared} />;
    case 'number-range':
      return <RangePicker def={def} {...shared} />;
    case 'date-range':
      return <RangePicker def={def} {...shared} />;
  }
}

interface PickerProps<TDef> {
  def: TDef;
  value: FilterValue | undefined;
  onChange: (next: FilterValue | undefined) => void;
  onDone?: () => void;
}

/* -------------------------------------------------------------------------- */
/* Option lists                                                               */
/* -------------------------------------------------------------------------- */

type ListOp = 'in' | 'not_in';

function listValues(value: FilterValue | undefined): string[] {
  return value && (value.op === 'in' || value.op === 'not_in') ? value.values : [];
}

function listOp(value: FilterValue | undefined): ListOp | undefined {
  return value && (value.op === 'in' || value.op === 'not_in') ? value.op : undefined;
}

type OptionListProps = PickerProps<EnumFilterDef<never> | AsyncFilterDef<never>> & {
  options: readonly FilterOption[];
  /** Chosen values missing from `options` (async: another page, or the URL). */
  extraSelected?: readonly FilterOption[];
  /** Remote search: the caller filters. Otherwise the list filters itself. */
  search?: string;
  onSearchChange?: (search: string) => void;
  status?: 'loading' | 'error' | 'ready';
};

/** Options whose label or description contains the search text. */
function matchOptions(options: readonly FilterOption[], search: string): readonly FilterOption[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return options;
  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(needle) ||
      option.description?.toLowerCase().includes(needle),
  );
}

/**
 * Checkboxes (or radios, for single choice) in a listbox with a search box
 * on top. Arrow keys move, Enter or Space toggles, and focus never leaves the
 * search box, so typing narrows the list at any time (once typing pauses).
 */
function OptionList({
  def,
  options,
  extraSelected = [],
  value,
  onChange,
  onDone,
  search: remoteSearch,
  onSearchChange,
  status = 'ready',
}: OptionListProps) {
  const { t } = useTranslation('common');
  const id = React.useId();
  const multiple = def.multiple !== false;
  const selected = listValues(value);
  const [localOp, setLocalOp] = React.useState<ListOp>(listOp(value) ?? 'in');
  const op = listOp(value) ?? localOp;
  const remote = onSearchChange !== undefined;
  const [localSearch, setLocalSearch] = React.useState('');
  const search = remote ? (remoteSearch ?? '') : localSearch;
  const [highlight, setHighlight] = React.useState(0);
  // The list narrows once typing pauses (a remote search debounces itself).
  const settled = useDebouncedSearch(search);

  const all = React.useMemo(
    () => [
      ...extraSelected.filter((option) => !options.some((o) => o.value === option.value)),
      ...options,
    ],
    [extraSelected, options],
  );
  const listed = React.useMemo(
    () => (remote ? all : matchOptions(all, settled.value)),
    [all, remote, settled.value],
  );

  const active = Math.min(highlight, Math.max(listed.length - 1, 0));

  const toggle = (optionValue: string) => {
    if (!multiple) {
      onChange({ op, values: [optionValue] });
      onDone?.();
      return;
    }
    const next = selected.includes(optionValue)
      ? selected.filter((v) => v !== optionValue)
      : [...selected, optionValue];
    onChange(next.length > 0 ? { op, values: next } : undefined);
  };

  const changeOp = (next: ListOp) => {
    setLocalOp(next);
    if (selected.length > 0) onChange({ op: next, values: selected });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Keys that act on the list first catch it up with what was typed.
    if (
      !remote &&
      settled.pending &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter')
    ) {
      settled.flush();
      const fresh = matchOptions(all, search);
      if (event.key === 'Enter') {
        const option = fresh[0];
        if (!option) return;
        event.preventDefault();
        toggle(option.value);
        return;
      }
      event.preventDefault();
      setHighlight(event.key === 'ArrowDown' ? 0 : Math.max(fresh.length - 1, 0));
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((active + 1) % Math.max(listed.length, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((active - 1 + listed.length) % Math.max(listed.length, 1));
    } else if (event.key === 'Enter' || (event.key === ' ' && search === '')) {
      const option = listed[active];
      if (!option) return;
      event.preventDefault();
      toggle(option.value);
    }
  };

  const listboxId = `${id}-options`;
  const optionId = (index: number) => `${id}-option-${index}`;

  return (
    <div data-slot="data-table-filter-options" className="flex w-64 flex-col">
      {def.negatable ? (
        <div
          role="radiogroup"
          aria-label={t('indexTable.picker.condition', { label: def.label })}
          className="flex gap-1 border-b p-1.5"
        >
          {(
            [
              ['in', t('indexTable.summary.verb.is')],
              ['not_in', t('indexTable.summary.verb.isNot')],
            ] as const
          ).map(([operator, label]) => (
            <button
              key={operator}
              type="button"
              role="radio"
              aria-checked={op === operator}
              onClick={() => changeOp(operator)}
              className={cn(
                'focus-visible:ring-ring h-7 flex-1 rounded-sm px-2 text-sm outline-none focus-visible:ring-2',
                op === operator
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex items-center gap-2 border-b px-2.5">
        <SearchIcon aria-hidden className="text-muted-foreground size-4 shrink-0" />
        <input
          autoFocus
          role="combobox"
          aria-expanded
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={listed.length > 0 ? optionId(active) : undefined}
          aria-label={t('indexTable.picker.search', { label: def.label })}
          placeholder={t('indexTable.picker.search', { label: def.label.toLowerCase() })}
          value={search}
          onChange={(event) => {
            setHighlight(0);
            if (remote) onSearchChange(event.target.value);
            else setLocalSearch(event.target.value);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          className="placeholder:text-muted-foreground h-9 min-w-0 flex-1 bg-transparent text-sm outline-none"
        />
      </div>
      <div
        id={listboxId}
        role="listbox"
        aria-label={def.label}
        aria-multiselectable={multiple || undefined}
        className="max-h-64 overflow-y-auto p-1"
      >
        {status === 'loading' && listed.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            {t('indexTable.picker.loading')}
          </p>
        ) : status === 'error' ? (
          <p role="alert" className="text-destructive py-6 text-center text-sm">
            {t('indexTable.picker.loadError', { label: def.label.toLowerCase() })}
          </p>
        ) : listed.length === 0 ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            {t('indexTable.picker.noMatches')}
          </p>
        ) : (
          listed.map((option, index) => {
            const checked = selected.includes(option.value);
            return (
              // Combobox pattern: the input keeps focus and handles keys.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- see above
              <div
                key={option.value}
                id={optionId(index)}
                role="option"
                aria-selected={checked}
                data-highlighted={index === active || undefined}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setHighlight(index)}
                onClick={() => toggle(option.value)}
                className="data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground flex min-h-8 cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none"
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center border',
                    multiple ? 'rounded-[4px]' : 'rounded-full',
                    checked
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background',
                  )}
                >
                  {checked ? <CheckIcon className="size-3" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.label}</span>
                  {option.description ? (
                    <span className="text-muted-foreground block truncate text-xs">
                      {option.description}
                    </span>
                  ) : null}
                </span>
                {option.count !== undefined ? (
                  <span className="text-muted-foreground text-xs tabular-nums">{option.count}</span>
                ) : null}
              </div>
            );
          })
        )}
      </div>
      {selected.length > 0 ? (
        <div className="flex justify-end border-t p-1.5">
          <Button variant="ghost" size="sm" onClick={() => onChange(undefined)}>
            {t('indexTable.picker.clear')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function EnumPicker(props: PickerProps<EnumFilterDef<never>>) {
  return <OptionList {...props} options={props.def.options} />;
}

function AsyncPicker(props: PickerProps<AsyncFilterDef<never>>) {
  const [search, setSearch] = React.useState('');
  const { options, isLoading, isError } = useFilterOptions(props.def, search);
  const selectedValues = listValues(props.value);
  const resolved = useResolvedOptions(props.def, selectedValues);
  const extraSelected = selectedValues.map(
    (value) => resolved.get(value) ?? { value, label: value },
  );
  return (
    <OptionList
      {...props}
      options={options}
      extraSelected={extraSelected}
      search={search}
      onSearchChange={setSearch}
      status={isError ? 'error' : isLoading ? 'loading' : 'ready'}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Text, boolean, ranges                                                      */
/* -------------------------------------------------------------------------- */

/** Clear and Apply. Apply submits the picker's form, so Enter in any field applies. */
function PickerFooter({ canClear, onClear }: { canClear: boolean; onClear: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex justify-end gap-2">
      {canClear ? (
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          {t('indexTable.picker.clear')}
        </Button>
      ) : null}
      <Button type="submit" size="sm">
        {t('indexTable.picker.apply')}
      </Button>
    </div>
  );
}

function TextPicker({ def, value, onChange, onDone }: PickerProps<TextFilterDef<never>>) {
  const { t } = useTranslation('common');
  const id = React.useId();
  const current = value?.op === 'contains' ? value.value : '';
  const [text, setText] = React.useState(current);
  const apply = () => {
    const trimmed = text.trim();
    onChange(trimmed ? { op: 'contains', value: trimmed } : undefined);
    onDone?.();
  };
  return (
    <form
      data-slot="data-table-filter-text"
      className="flex w-64 flex-col gap-2 p-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
    >
      <label htmlFor={id} className="text-muted-foreground text-xs font-medium">
        {t('indexTable.picker.contains', { label: def.label })}
      </label>
      <Input
        id={id}
        autoFocus
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={def.placeholder}
      />
      <PickerFooter
        canClear={current !== ''}
        onClear={() => {
          setText('');
          onChange(undefined);
        }}
      />
    </form>
  );
}

function BooleanPicker({ def, value, onChange, onDone }: PickerProps<BooleanFilterDef<never>>) {
  const { t } = useTranslation('common');
  const current = value?.op === 'eq' ? value.value : undefined;
  const choices: Array<[boolean, string]> = [
    [true, def.trueLabel ?? t('indexTable.boolean.yes')],
    [false, def.falseLabel ?? t('indexTable.boolean.no')],
  ];
  return (
    <div
      data-slot="data-table-filter-boolean"
      role="radiogroup"
      aria-label={def.label}
      className="flex w-56 flex-col p-1"
    >
      {choices.map(([choice, label], index) => (
        <button
          key={label}
          type="button"
          role="radio"
          aria-checked={current === choice}
          autoFocus={index === 0}
          onClick={() => {
            onChange({ op: 'eq', value: choice });
            onDone?.();
          }}
          className="hover:bg-accent focus-visible:bg-accent focus-visible:ring-ring flex min-h-8 items-center gap-2 rounded-sm px-2 text-left text-sm outline-none focus-visible:ring-2"
        >
          <span
            aria-hidden
            className={cn(
              'flex size-4 shrink-0 items-center justify-center rounded-full border',
              current === choice
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-input bg-background',
            )}
          >
            {current === choice ? <CheckIcon className="size-3" /> : null}
          </span>
          {label}
        </button>
      ))}
      {current !== undefined ? (
        <div className="mt-1 flex justify-end border-t pt-1">
          <Button variant="ghost" size="sm" onClick={() => onChange(undefined)}>
            {t('indexTable.picker.clear')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RangePicker({
  def,
  value,
  onChange,
  onDone,
}: PickerProps<NumberRangeFilterDef<never> | DateRangeFilterDef<never>>) {
  const { t } = useTranslation('common');
  const id = React.useId();
  const isDate = def.type === 'date-range';
  const current = value?.op === 'between' ? value : undefined;
  // Inputs hold what the person types: major units for a money range.
  const shown = (bound: string | undefined) => (bound ? toDisplayBound(def, bound) : '');
  const [min, setMin] = React.useState(shown(current?.min));
  const [max, setMax] = React.useState(shown(current?.max));
  // Resync when the value changes from outside (chip removed, typed qualifier).
  const [synced, setSynced] = React.useState(value);
  if (synced !== value) {
    setSynced(value);
    setMin(shown(current?.min));
    setMax(shown(current?.max));
  }

  const presets =
    def.type === 'date-range'
      ? def.presets === false
        ? []
        : (def.presets ?? defaultDatePresets(t))
      : [];

  const commit = (nextMin: string, nextMax: string) => {
    // A bound that is not an amount is dropped, as a blank one is.
    const a = nextMin.trim() && (toStoredBound(def, nextMin.trim()) ?? '');
    const b = nextMax.trim() && (toStoredBound(def, nextMax.trim()) ?? '');
    onChange(
      a || b ? { op: 'between', ...(a ? { min: a } : {}), ...(b ? { max: b } : {}) } : undefined,
    );
  };

  // The date picker has no `autoFocus`; its ref reaches the first segment. This runs before
  // the popover's own initial focus, which then leaves the focused segment alone.
  const startRef = React.useRef<HTMLElement>(null);
  const focusStart = isDate && presets.length === 0;
  React.useEffect(() => {
    if (focusStart) startRef.current?.focus();
  }, [focusStart]);

  const bound = (side: 'min' | 'max') => {
    const fieldId = `${id}-${side}`;
    const fieldValue = side === 'min' ? min : max;
    const setValue = side === 'min' ? setMin : setMax;
    if (def.type === 'date-range') {
      // Reports '' while a date is partly typed, as the native date input did.
      return (
        <DatePicker
          id={fieldId}
          ref={side === 'min' ? startRef : undefined}
          value={fieldValue}
          onChange={setValue}
        />
      );
    }
    return (
      <Input
        id={fieldId}
        autoFocus={side === 'min' && presets.length === 0}
        value={fieldValue}
        onChange={(event) => setValue(event.target.value)}
        placeholder={def.prefix ?? def.suffix ?? def.currency}
        type="number"
        inputMode="decimal"
        min={def.min}
        max={def.max}
        step={def.step ?? 'any'}
      />
    );
  };

  return (
    <form
      data-slot="data-table-filter-range"
      // Two date fields (segments and a calendar button) need a little more room than numbers.
      className={cn('flex flex-col gap-3 p-2.5', isDate ? 'w-80' : 'w-72')}
      onSubmit={(event) => {
        event.preventDefault();
        commit(min, max);
        onDone?.();
      }}
    >
      {presets.length > 0 ? (
        <div
          role="group"
          aria-label={t('indexTable.picker.presets', { label: def.label })}
          className="flex flex-col"
        >
          {presets.map((preset) => {
            const range = preset.range();
            const on =
              (current?.min ?? '') === (range.from ?? '') &&
              (current?.max ?? '') === (range.to ?? '');
            return (
              <button
                key={preset.id}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setMin(range.from ?? '');
                  setMax(range.to ?? '');
                  commit(range.from ?? '', range.to ?? '');
                  onDone?.();
                }}
                className="hover:bg-accent focus-visible:ring-ring flex min-h-8 items-center gap-2 rounded-sm px-2 text-left text-sm outline-none focus-visible:ring-2"
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-full border',
                    on
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background',
                  )}
                >
                  {on ? <CheckIcon className="size-3" /> : null}
                </span>
                {preset.label}
              </button>
            );
          })}
        </div>
      ) : null}
      <fieldset className="grid grid-cols-2 gap-2">
        <legend className="text-muted-foreground mb-1.5 text-xs font-medium">
          {presets.length > 0 ? t('indexTable.picker.customRange') : def.label}
        </legend>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-min`} className="text-muted-foreground text-xs">
            {isDate ? t('indexTable.picker.starting') : t('indexTable.picker.min')}
          </label>
          {bound('min')}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-max`} className="text-muted-foreground text-xs">
            {isDate ? t('indexTable.picker.ending') : t('indexTable.picker.max')}
          </label>
          {bound('max')}
        </div>
      </fieldset>
      <PickerFooter
        canClear={current !== undefined}
        onClear={() => {
          setMin('');
          setMax('');
          onChange(undefined);
        }}
      />
    </form>
  );
}

export { DataTableFilterPicker };
